import classnames from "classnames";
import { useMemo } from "react";
import { FormatNumber, ucFirst } from "utils";
import { useFormatCurrency } from "utils/customHooks/useFormatCurrency";

import type { Currency } from "@krea/shared/types/common";

import type { ILeasingBid } from "@/features/lender-responses/types/lender-response";

import type { IBidDetailsResponse } from "../../BidDetails";
import {
  BidDetailsListItem,
  BidFeesIncludedNote,
  EmptyField,
} from "../../bidHelpers";

interface ILeasingBidProps {
  bid: ILeasingBid;
  currency: Currency;
}

export const LeasingBid = ({
  bid,
  currency,
}: ILeasingBidProps): IBidDetailsResponse => {
  const [formattedCurrencyValue] = useFormatCurrency({ currency });

  const interestMonthly = bid.comparableMonthlyInterestRate;

  const details = useMemo(() => {
    return (
      <div>
        {!!bid.downPayment && (
          <BidDetailsListItem
            title="Down payment"
            value={formattedCurrencyValue(bid.downPayment)}
          />
        )}

        {(!!bid.terminalValue ||
          !!bid.averageMonthlyCost ||
          !!bid.averageMonthlyAmortisation) && (
          <>
            {!!bid.terminalValue && (
              <BidDetailsListItem
                title="Terminal value"
                value={bid.terminalValue}
              />
            )}
            {!!bid.averageMonthlyCost && (
              <BidDetailsListItem
                title="Interest payment"
                value={bid.averageMonthlyCost}
              />
            )}
            {!!bid.averageMonthlyAmortisation && (
              <BidDetailsListItem
                title="Principal repayment"
                value={bid.averageMonthlyAmortisation}
              />
            )}
          </>
        )}

        {!!bid.setupFee && (
          <BidDetailsListItem
            title="Setup fee"
            value={formattedCurrencyValue(bid.setupFee)}
          />
        )}

        <BidFeesIncludedNote
          className={classnames({
            "mb-3 border-b pb-3":
              !!bid.administrationFees || !!bid.insuranceMonthlyCost,
          })}
        />

        <BidDetailsListItem
          title="Type of contract"
          value={ucFirst(bid.contractType)}
        />

        {!!bid.administrationFees && (
          <BidDetailsListItem
            title="Administration fees (per month)"
            value={formattedCurrencyValue(bid.administrationFees)}
          />
        )}

        {!!bid.insuranceMonthlyCost && (
          <BidDetailsListItem
            title="Insurance cost (per month)"
            value={formattedCurrencyValue(bid.insuranceMonthlyCost)}
          />
        )}
      </div>
    );
  }, [bid, formattedCurrencyValue]);

  const summary = useMemo(() => {
    return (
      <div className="flex w-full flex-col gap-2 xl:grid xl:grid-cols-4 xl:gap-4">
        <div className="flex flex-col gap-4">
          <BidDetailsListItem
            variant="summary"
            title="Amount"
            value={
              bid.amount ? formattedCurrencyValue(bid.amount) : <EmptyField />
            }
          />
        </div>

        <div className="flex flex-col gap-4">
          <BidDetailsListItem
            variant="summary"
            title="Repayment time"
            value={
              bid.paybackTimeMonths ? (
                `${bid.paybackTimeMonths} ${bid.paybackTimeMonths > 1 ? "months" : "month"}`
              ) : (
                <EmptyField />
              )
            }
          />
        </div>
        <div className="flex flex-col gap-4">
          <BidDetailsListItem
            variant="summary"
            title="Monthly payment"
            value={
              bid.monthlyPayment ? (
                formattedCurrencyValue(bid.monthlyPayment)
              ) : (
                <EmptyField />
              )
            }
          />
        </div>
        <div className="flex flex-col gap-4">
          <BidDetailsListItem
            variant="summary"
            title="Effective monthly interest"
            value={
              interestMonthly ? (
                `${FormatNumber(interestMonthly)}%`
              ) : (
                <EmptyField />
              )
            }
          />
        </div>
      </div>
    );
  }, [bid, formattedCurrencyValue, interestMonthly]);

  return {
    summary,
    details,
  };
};
